/**
 * utils/sseParser.ts
 * Server-Sent Events (SSE) stream parser
 *
 * Parses SSE streams from fetch Response objects. Handles both OpenAI
 * format (data-only lines, `data: [DONE]` sentinel) and Anthropic format
 * (typed events with `event:` + `data:` lines).
 *
 * Work Unit: WU04 - AI Provider Client Implementations
 * Task: T01 - OpenAI Provider Client
 * Sprint: S01 - AI Architecture & Infrastructure Foundation
 * Project: P09 - AI Integration
 */

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Parsed SSE event
 *
 * @see https://html.spec.whatwg.org/multipage/server-sent-events.html
 */
export interface SSEEvent {
  /** Event data payload (concatenated from all `data:` lines) */
  data: string;

  /** Optional event type (from `event:` line) */
  event?: string;
}

// ─── Parser ─────────────────────────────────────────────────────────────────

/**
 * Parse an SSE stream from a fetch Response.
 *
 * Reads the response body as text, splits on double-newline boundaries,
 * and calls `onEvent` for each parsed SSE event. Handles partial chunks
 * that span multiple `ReadableStream.read()` calls.
 *
 * @param response - Fetch Response with a readable body stream
 * @param onEvent - Callback invoked for each parsed SSE event
 * @throws Error if the response body is null
 *
 * @example
 * ```ts
 * await parseSSEStream(response, (event) => {
 *   if (event.data === '[DONE]') return; // OpenAI sentinel
 *   const parsed = JSON.parse(event.data);
 *   console.log(parsed);
 * });
 * ```
 */
export async function parseSSEStream(
  response: Response,
  onEvent: (event: SSEEvent) => void
): Promise<void> {
  const body = response.body;
  if (!body) {
    throw new Error('Response body is null');
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining buffered data
        if (buffer.trim().length > 0) {
          const event = parseEventBlock(buffer);
          if (event) {
            onEvent(event);
          }
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Normalize CRLF and standalone CR to LF per WHATWG SSE spec
      buffer = buffer.replace(/\r\n?/g, '\n');

      // Split on double-newline (SSE event boundary)
      const events = buffer.split('\n\n');

      // Last element may be incomplete — keep it in the buffer
      buffer = events.pop() ?? '';

      for (const block of events) {
        if (block.trim().length === 0) continue;

        const event = parseEventBlock(block);
        if (event) {
          onEvent(event);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Create an async iterable from an SSE stream.
 *
 * Wraps `parseSSEStream` into an async generator that yields
 * `SSEEvent` objects as they arrive.
 *
 * @param response - Fetch Response with a readable body stream
 * @returns Async iterable of parsed SSE events
 *
 * @example
 * ```ts
 * for await (const event of createSSEIterable(response)) {
 *   if (event.data === '[DONE]') break;
 *   const chunk = JSON.parse(event.data);
 *   process.stdout.write(chunk.choices[0].delta.content ?? '');
 * }
 * ```
 */
export async function* createSSEIterable(
  response: Response
): AsyncGenerator<SSEEvent, void, undefined> {
  const body = response.body;
  if (!body) {
    throw new Error('Response body is null');
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining buffered data
        if (buffer.trim().length > 0) {
          const event = parseEventBlock(buffer);
          if (event) {
            yield event;
          }
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Normalize CRLF and standalone CR to LF per WHATWG SSE spec
      buffer = buffer.replace(/\r\n?/g, '\n');

      // Split on double-newline (SSE event boundary)
      const events = buffer.split('\n\n');

      // Last element may be incomplete — keep it in the buffer
      buffer = events.pop() ?? '';

      for (const block of events) {
        if (block.trim().length === 0) continue;

        const event = parseEventBlock(block);
        if (event) {
          yield event;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ─── Internal ───────────────────────────────────────────────────────────────

/**
 * Parse a single SSE event block into an SSEEvent.
 *
 * Handles `data:`, `event:`, and comment (`:`) lines per the SSE spec.
 * Multiple `data:` lines are concatenated with newlines.
 *
 * @param block - Raw text of a single SSE event (between \n\n boundaries)
 * @returns Parsed event, or null if the block contains no data
 */
function parseEventBlock(block: string): SSEEvent | null {
  const lines = block.split('\n');
  const dataLines: string[] = [];
  let eventType: string | undefined;

  for (const line of lines) {
    // Comment lines start with ':'
    if (line.startsWith(':')) continue;

    if (line.startsWith('data:')) {
      // Strip 'data:' prefix and optional leading space
      dataLines.push(line.slice(5).trimStart());
    } else if (line.startsWith('event:')) {
      // Strip 'event:' prefix and optional leading space
      eventType = line.slice(6).trimStart();
    }
    // Ignore 'id:' and 'retry:' lines — not needed for AI streaming
  }

  if (dataLines.length === 0) return null;

  const event: SSEEvent = {
    data: dataLines.join('\n'),
  };

  if (eventType != null) {
    event.event = eventType;
  }

  return event;
}
